/**
 * Disposal prices page — data hooks (Phase 28 / task 28.5a, DEC-331). The API is the gate
 * (MANAGE_DISPOSAL_PRICES); these make no trust decisions.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type {
  AddPriceBody,
  DisposalPlace,
  DisposalPricesResponse,
  DisposalRecSettings,
  DispatcherDisplay,
  DriverDisplay,
} from './disposal-prices';

export const DISPOSAL_PRICES_KEY = ['billing', 'disposal-prices'] as const;
export const DISPOSAL_REC_SETTINGS_KEY = ['billing', 'disposal-rec-settings'] as const;

export function useDisposalPrices(): ReturnType<typeof useApiQuery<DisposalPricesResponse>> {
  const opts = useApiOpts();
  return useApiQuery<DisposalPricesResponse>(opts, '/v1/disposal-prices', {
    queryKey: DISPOSAL_PRICES_KEY,
    staleTime: 30_000,
  });
}

/** Places matching `q` (facilities + the company's drop-off locations), at most 50. */
export function useDisposalPlaces(
  q: string,
): ReturnType<typeof useApiQuery<{ places: DisposalPlace[] }>> {
  const opts = useApiOpts();
  const term = q.trim();
  return useApiQuery<{ places: DisposalPlace[] }>(
    opts,
    `/v1/disposal-prices/places?q=${encodeURIComponent(term)}`,
    { queryKey: ['billing', 'disposal-places', term], staleTime: 30_000 },
  );
}

export function useAddDisposalPrice(
  onDone: () => void,
): ReturnType<typeof useApiMutation<AddPriceBody, { id: string }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<AddPriceBody, { id: string }>(opts, '/v1/disposal-prices', 'POST', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DISPOSAL_PRICES_KEY });
      void qc.invalidateQueries({ queryKey: ['billing', 'disposal-places'] });
      onDone();
    },
  });
}

export function useDisposalRecSettings(): ReturnType<
  typeof useApiQuery<{ settings: DisposalRecSettings }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ settings: DisposalRecSettings }>(
    opts,
    '/v1/tenants/disposal-recommendation-settings',
    { queryKey: DISPOSAL_REC_SETTINGS_KEY, staleTime: 30_000 },
  );
}

export interface DisposalRecSettingsPatch {
  readonly enabled?: boolean;
  readonly driverDisplay?: DriverDisplay;
  readonly dispatcherDisplay?: DispatcherDisplay;
  readonly typicalLoadBbl?: number;
}

export function useSaveDisposalRecSettings(): ReturnType<
  typeof useApiMutation<DisposalRecSettingsPatch, { settings: DisposalRecSettings }>
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<DisposalRecSettingsPatch, { settings: DisposalRecSettings }>(
    opts,
    '/v1/tenants/disposal-recommendation-settings',
    'PATCH',
    {
      onSuccess: (result) => {
        qc.setQueryData(DISPOSAL_REC_SETTINGS_KEY, result);
      },
    },
  );
}
